import { combineEpics, ofType } from "redux-observable";
import {
  catchError,
  concatMap,
  endWith,
  map,
  mergeMap,
  startWith,
  switchMap,
  withLatestFrom,
} from "rxjs/operators";
import { EMPTY, from, of } from "rxjs";
import { RootState } from "../../store/ConfigureStore";
import { copyJetliftProject as copyJetliftProjectService, deleteJetliftCase, getCalculations, saveProject, SaveJetLiftMainReportData, GetJetliftCaseComparisonData, downloadJLMainReport as downloadJLMainReportService } from "../../services/jetlift/jetliftCalculations.service";
import { getProjectAndAffiliations } from "../../store/calculation/calculation-input/ProjectHeaderSlice";
import { setWellTabDetails } from "../../store/jetlift/jetlift-input/WellSlice";
import { setProjectTabDetails } from "../../store/calculation/calculation-input/ProjectTabSlice";
import { setFluidTabDetails, setRegion, setRegionList } from "../../store/jetlift/jetlift-input/FluidSlice";
import { setTubingDetails, setInputUnits, setCatalogType2 } from "../../store/jetlift/jetlift-input/TubingSlice"
import { setOtherDetails } from "../../store/jetlift/jetlift-input/OthersSlice";
import { setJetPumpInput } from "../../store/jetlift/jetlift-input/JetPumpSlice";
import { emptyaction, setLoading, saveJetliftMainReportData as saveJetliftMainReportDataAction } from "../../store/jetlift/JetliftCalculationSlice";
import { setJetliftProjectID, setLiftOption } from "../../store/dashboard/CreateProjectSlice";
import { WellSurveyServerData } from "../../store/model/calculations/calculation";
import { saveProjectPayload } from "../../store/model/jetlift/jetlift";
import { toast } from "react-toastify";
import { gasCalculationStart } from "../../store/jetlift/jetlift-output/GasCalculationSlice";
import { cntrlList, inflowType, manufactureList, powerFluidTypeList, suplPwrList } from "../../constants/jetlift/jetlift";
import { getJetLiftMainReportDataFromUtility } from "../../utilities/jetliftUtilities";
import { reverseTransform, getJLReportDataFromUtility } from "../../utilities/commonUtilities";
import { setComparisonData1, setComparisonData2, setComparisonData3, setComparisonInput1, setComparisonInput2, setComparisonInput3, setPersistedCase1, setPersistedCase2, setPersistedCase3 } from "../../store/jetlift/comparison/ComparisonSlice";
import { setShowIPRPlotInMainReport } from "../../store/jetlift/jetlift-output/BasicCalculationSlice";

const fetchProjectDetailsEpic = (action$: any, state$: any) => {
  return action$.pipe(
    ofType("jetlift/calculations/getProjectDetails"),
    withLatestFrom(
      state$.pipe(
        map((state: RootState) => state?.myProjects?.selectedProject)
      ),
      state$.pipe(map((state: RootState) => state?.createProject?.template))
    ),
    concatMap(([actionData, selectedProject, selectedTemplate]) => {
      return from(
        getCalculations(
          actionData
            ? {
              id: actionData?.payload?.id,
              templateId: actionData?.payload?.templateid,
            }
            : { id: selectedProject?.projectID, templateId: selectedTemplate }
        )
      ).pipe(
        map((result) => {
          var output = {
            result,
          };
          return output;
        }),
        mergeMap((output) => {
          const actions: any[] = [
            setJetliftProjectID({ projectID: output?.result?.projectID, templateID: "" }),
            setShowIPRPlotInMainReport(output?.result?.showIPRPlotInMainReport),
            setInputUnits(output?.result?.units),
            setProjectTabDetails(output?.result?.projectSettingsInput),
            setComparisonInput1(output?.result?.projectSettingsInput?.comparisonCaseId1),
            setComparisonInput2(output?.result?.projectSettingsInput?.comparisonCaseId2),
            setComparisonInput3(output?.result?.projectSettingsInput?.comparisonCaseId3),
            setPersistedCase1(output?.result?.projectSettingsInput?.comparisonCaseId1),
            setPersistedCase2(output?.result?.projectSettingsInput?.comparisonCaseId2),
            setPersistedCase3(output?.result?.projectSettingsInput?.comparisonCaseId3),
            setWellTabDetails(output?.result?.wellInput),
            setTubingDetails(output?.result?.tubingTab),
            setFluidTabDetails(output?.result?.fluidInput),
            setRegion(output?.result?.fluidInput?.region?.selectedId),
            setRegionList(output?.result?.fluidInput?.region?.regionList),
            setOtherDetails(output?.result?.othersInput),
            setJetPumpInput(output?.result?.jetPumpInput),
            gasCalculationStart(),
            { type: "jetlift/wellSurvey/fetchExistingData", payload: { projectId: output?.result?.projectID } },
          ];

          // Set well survey metadata from WellSurveyESPAssembly if available
          if (output?.result?.wellSurveyESPAssembly) {
            const metadata = output.result.wellSurveyESPAssembly;
            if (metadata.kOP !== undefined && metadata.kOP !== null) {
              actions.push({ type: "wellSurvey/setWellKOPOverride", payload: metadata.kOP });
            }
            if (metadata.espLength !== undefined && metadata.espLength !== null) {
              actions.push({ type: "wellSurvey/setEspLength", payload: metadata.espLength });
            }
            if (metadata.bottomOfESPSensor !== undefined && metadata.bottomOfESPSensor !== null) {
              actions.push({ type: "wellSurvey/setBottomEspSensor", payload: metadata.bottomOfESPSensor });
            }
            if (metadata.tailPipeDesander !== undefined && metadata.tailPipeDesander !== null) {
              actions.push({ type: "wellSurvey/setTailPipeDesander", payload: metadata.tailPipeDesander });
            }
            if (metadata.showWellDataInMainReport !== undefined) {
              actions.push({ type: "wellSurvey/setWellDataInMainReportFlag", payload: metadata.showWellDataInMainReport });
            }
            if (metadata.notes) {
              actions.push({ type: "wellSurvey/setNotes", payload: metadata.notes });
            }
          }

          return actions;
        }),
        catchError((error) => {
          return of({
            type: "calculations/fetchProjectDetailsError",
            payload: error.message,
          });
        })
      );
    })
  );
};

const calculationsTemplate = (action$: any, state$: any) => {
  return action$.pipe(
    ofType("jetlift/calculations/fetchTemplateCalculations"),
    withLatestFrom(
      state$.pipe(map((state: RootState) => state?.createProject?.jetliftProjectID)),
      state$.pipe(map((state: RootState) => state?.createProject))
    ),
    switchMap(([actionData, ID, projectdata]) => {
      return from(
        getCalculations({
          id: "",
          templateId: actionData?.payload?.templateId || ID?.templateID || "",
          unitSystem: actionData?.payload?.unitSystem || 0,
        })
      ).pipe(
        mergeMap((data) => {
          if (data) {
            const actions: any[] = [
              setJetliftProjectID({
                projectID: data?.projectID,
                templateID: actionData?.payload?.templateId || ID?.templateID || ""
              }),
              setShowIPRPlotInMainReport(data?.showIPRPlotInMainReport),
              setLiftOption({ designType: "Jet lift", id: data?.liftType }),
              setProjectTabDetails({
                ...projectdata,
                wellName:
                  typeof projectdata?.well === "string"
                    ? projectdata?.well
                    : projectdata?.wellList.find(
                      (x: any) => x?.value === projectdata?.well
                    )?.label,
                wellApiNo: projectdata?.wellApiNo,
                countryList: projectdata?.countryList,
                caseName: projectdata?.caseName,
                companyName:
                  typeof projectdata?.customer === "string"
                    ? projectdata?.customer
                    : projectdata?.customerList.find(
                      (x: any) => x?.value === projectdata.customer
                    )?.label,
              }),
              setInputUnits(data?.units),
              setWellTabDetails(data?.wellInput),
              setFluidTabDetails(data?.fluidInput),
              setTubingDetails(data?.tubingTab),
              setCatalogType2(2),
              setJetPumpInput(data?.jetPumpInput),
              setRegion(data?.fluidInput?.region?.selectedId),
              setRegionList(data?.fluidInput?.region?.regionList),
              setOtherDetails(data?.othersInput),
            ];

            // Set well survey metadata from WellSurveyESPAssembly if available
            if (data?.wellSurveyESPAssembly) {
              const metadata = data.wellSurveyESPAssembly;
              if (metadata.kOP !== undefined && metadata.kOP !== null) {
                actions.push({ type: "wellSurvey/setWellKOPOverride", payload: metadata.kOP });
              }
              if (metadata.espLength !== undefined && metadata.espLength !== null) {
                actions.push({ type: "wellSurvey/setEspLength", payload: metadata.espLength });
              }
              if (metadata.bottomOfESPSensor !== undefined && metadata.bottomOfESPSensor !== null) {
                actions.push({ type: "wellSurvey/setBottomEspSensor", payload: metadata.bottomOfESPSensor });
              }
              if (metadata.tailPipeDesander !== undefined && metadata.tailPipeDesander !== null) {
                actions.push({ type: "wellSurvey/setTailPipeDesander", payload: metadata.tailPipeDesander });
              }
              if (metadata.showWellDataInMainReport !== undefined) {
                actions.push({ type: "wellSurvey/setWellDataInMainReportFlag", payload: metadata.showWellDataInMainReport });
              }
              if (metadata.notes) {
                actions.push({ type: "wellSurvey/setNotes", payload: metadata.notes });
              }
            }

            return actions;
          } else {
            return of({ type: "" });
          }
        }),
        catchError((error) => {
          return of(console.error(error));
        }),
        startWith(setLoading(true)),
        endWith((setLoading(false))),
      );
    })
  );
};

const SaveProject = (action$: any, state$: any) => {
  return action$.pipe(
    ofType("jetlift/calculations/saveJetliftProject"),
    withLatestFrom(
      state$.pipe(map((state: RootState) => state?.jetlift?.tubing?.tubingInput)),
      state$.pipe(map((state: RootState) => state?.jetlift?.well?.wellInput)),
      state$.pipe(map((state: RootState) => state?.jetlift?.fluid?.fluidInput)),
      state$.pipe(map((state: RootState) => state?.jetlift?.jetPump?.jetPump)),
      state$.pipe(map((state: RootState) => state?.projectTab?.tab4Input)),
      state$.pipe(map((state: RootState) => state?.jetlift?.others?.othersInput)),
      state$.pipe(map((state: RootState) => state?.jetlift?.basicCalculations?.depletionPressure)),
      state$.pipe(map((state: RootState) => state?.jetlift?.basicCalculations?.futureProductionRate)),
      state$.pipe(map((state: RootState) => state?.jetlift?.basicCalculations?.showDepletionPlotInMainReport)),
      state$.pipe(map((state: RootState) => state?.jetlift?.basicCalculations?.showIPRPlotInMainReport)),
      state$.pipe(map((state: RootState) => state?.jetlift?.wellSurvey?.wellSurveyData)),
      state$.pipe(map((state: RootState) => state?.jetlift?.wellSurvey)),
      state$.pipe(map((state: RootState) => state?.jetlift?.fluid?.fluidInput?.region?.regionList)),
      state$.pipe(map((state: RootState) => state?.jetlift?.fluid?.region)),
      state$.pipe(map((state: RootState) => state?.createProject)),
      state$.pipe(map((state: RootState) => state?.createProject?.jetliftProjectID?.templateID)),
      state$.pipe(map((state: RootState) => state?.createProject?.jetliftProjectID?.projectID)),
      state$.pipe(map((state: RootState) => state?.projectHeader?.projectAndAffiliations)),
      state$.pipe(map((state: RootState) => state?.jetlift?.comparison)),
      state$.pipe(map((state: RootState) => state?.myProjects?.selectedProject?.projectID)),
    ),
    switchMap(
      ([
        actionData,
        tubingTab,
        wellInput,
        fluidInput,
        jetPumpInput,
        projectData,
        othersInput,
        depletionPressure,
        futureProductionRate,
        showDepletionPlotInMainReport,
        showIPRPlotInMainReport,
        wellSurveyData,
        wellSurveyState,
        regionList,
        selectedRegion,
        ProjectDetails,
        templateID,
        DefaultProjectID,
        projectAndAffiliations,
        caseComparison,
        parentProjectId,
      ]) => {
        const url = typeof window !== "undefined" ? window.location.href : "";
        const projectIdMatch = url.match(/projectID=([\w-]+)/);
        const parentProjectIdFromUrl = projectIdMatch && projectIdMatch[1] ? projectIdMatch[1] : "";
        const resolvedParentProjectId = parentProjectIdFromUrl || parentProjectId || projectData?.parentId || "";

        tubingTab = Object.assign(
          {},
          {
            ...tubingTab,
            catalogType: tubingTab?.catalogType || 0,
            Hdn_Pump_HousingType: tubingTab?.hdn_Pump_HousingType,
            Hdn_Pump_ShaftType: tubingTab?.hdn_Pump_ShaftType,
            HdnSurface_Efficiency: tubingTab?.hdnSurface_Efficiency,
            linerODList: {},
            tubingODList: {},
            tubingTypeList: {},
            casingODList: {},
            linerWeightList: {},
            casingWeightList: {},
            tubing1WeightList: {},
            tubing2WeightList: {},
            wellTrajectoryList: {},
            tubingTypeUpsetList: {},
          }
        );
        projectData = Object.assign(
          {},
          {
            ...projectData,
            companyName: ProjectDetails?.customer,
            customerContact: ProjectDetails?.contact,
            wellName: ProjectDetails?.well,
            country: ProjectDetails?.country,
            state: ProjectDetails?.state,
            dalRepr: ProjectDetails?.accountManager,
            formation: ProjectDetails?.formation,
            ProjectName: projectData?.projectName,
            countryList: {},
            stateList: {},
            countyList: {},
            parentId: resolvedParentProjectId,
          }
        );

        wellInput = Object.assign(
          {},
          {
            ...wellInput,
            setDepthType: wellInput?.setDepthType || 0,
            inflowType: inflowType,
          }
        );

        fluidInput = Object.assign(
          {},
          {
            ...fluidInput,
            region: {
              selectedId: selectedRegion,
              regionList: regionList,
            }
          }
        );

        othersInput = Object.assign(
          {},
          {
            ...othersInput,
            suplPwrList: suplPwrList,
            contrlList: cntrlList,
          }
        );
        const nozzleList = reverseTransform(jetPumpInput?.nozzleList);
        const throatList = reverseTransform(jetPumpInput?.throatList);

        jetPumpInput = Object.assign(
          {},
          {
            ...jetPumpInput,
            manufactureList: manufactureList,
            nozzleList: nozzleList,
            throatList: throatList,
            powerFluidTypeList: powerFluidTypeList,
            powerFluidDensity: jetPumpInput?.powerFluidType === "1" ? fluidInput?.spGrWater : fluidInput?.oilAPI,
          }
        );

        let NameSearch: any = {
          CompanyName: projectData?.companyName,
          CustomerContact: projectData?.customerContact,
          WellName: projectData?.wellName,
          Country: projectData?.country,
          State: projectData?.state,
          CatalogType: +tubingTab?.catalogType || 0,
          DalRep: projectData?.dalRepr,
          Formation: projectData?.formation,
          ProjectID: templateID ? DefaultProjectID : actionData?.payload?.id || "",
          showIPRPlotInMainReport: showIPRPlotInMainReport,
          tubingTab: tubingTab,
          wellInput: wellInput,
          fluidInput: fluidInput,
          projectData: projectData,
          jetPump: jetPumpInput,
          othersInput: othersInput,
        };

        let collection: any = {
          Lifttype: 2,
          showIPRPlotInMainReport: showIPRPlotInMainReport,
          tubingTab: tubingTab,
          wellInput: wellInput,
          fluidInput: fluidInput,
          projectData: projectData,
          othersInput: othersInput,
          jetPumpInput: jetPumpInput,
          ProjectData: {
            ProjectName: projectData?.projectName || "",
            CaseName: ProjectDetails?.caseName ?? "",
            CaseDescription: ProjectDetails?.caseDescription ?? "",
            UOM: ProjectDetails?.unitMeasurement ?? "",
            DALRep: ProjectDetails?.accountManager ?? "",
            CompanyName: ProjectDetails?.customer ?? "",
            OperatorContact: ProjectDetails?.contact ?? "",
            WellName: ProjectDetails?.well ?? "",
            WellAPI: ProjectDetails?.wellApiNo ?? "",
            Country: ProjectDetails?.country ?? "",
            State: ProjectDetails?.state ?? "",
            Formation: ProjectDetails?.formation ?? "",
            County: ProjectDetails?.county ?? "",
            CountyOther: ProjectDetails?.countyOther ?? "",
            parentId: resolvedParentProjectId,
            WellCnxId: [0, null]?.includes(ProjectDetails?.wellID) ? null : ProjectDetails?.wellID,
            CustomerCnxId: [0, null]?.includes(ProjectDetails?.customerID) ? null : ProjectDetails?.customerID,
            ComparisonCaseId1: caseComparison?.compareInput1 || "",
            ComparisonCaseId2: caseComparison?.compareInput2 || "",
            ComparisonCaseId3: caseComparison?.compareInput3 || "",
            DisplayCaseName: projectAndAffiliations?.filter((item: any) => item?.projectID === actionData?.payload?.id)?.[0]?.displayCaseName ?? "Case #1",
          },
        };

        let surveyData: WellSurveyServerData[] = [];
        if (wellSurveyData?.length) {
          surveyData = wellSurveyData.map(
            (s: { md: any; inc: any; dls: any }) => {
              return {
                MD: s.md,
                INC: s.inc,
                DLS: s.dls,
              };
            }
          );
        }

        if (
          (ProjectDetails?.projectName === "" && projectData?.projectName === "") ||
          (ProjectDetails?.projectName === undefined && projectData?.projectName === undefined)
        ) {
          return EMPTY;
        }
        if (templateID === "" || templateID === undefined) {
          NameSearch.tubingTab.opType = "Update";
        } else {
          NameSearch.tubingTab.opType = "Add";
        };

        var postData: saveProjectPayload = {
          Lifttype: collection?.Lifttype,
          projectID: templateID ? DefaultProjectID : actionData?.payload?.id || "",
          showIPRPlotInMainReport: collection?.showIPRPlotInMainReport,
          tubingTab: collection?.tubingTab,
          wellInput: collection?.wellInput,
          fluidInput: collection?.fluidInput,
          jetPumpInput: collection?.jetPumpInput,
          othersInput: collection?.othersInput,
          ProjectData: collection.ProjectData,
          WellSurveySamples: surveyData || null,
          ComparisonCaseId1: caseComparison?.compareInput1 || "",
          ComparisonCaseId2: caseComparison?.compareInput2 || "",
          ComparisonCaseId3: caseComparison?.compareInput3 || "",
          AIEnabled: false,
          WellSurveyESPAssembly: {
            ESPLength: wellSurveyState?.ESPLength || 0,
            kOP: wellSurveyState?.KOPOverride || 0,
            BottomOfESPSensor: wellSurveyState?.BottomOfESPSensor || 0,
            TailPipeDesander: wellSurveyState?.TailPipeDesander || 0,
            ShowWellDataInMainReport: wellSurveyState?.showWellDataInMainReport || false,
            Notes: wellSurveyState?.wellSurvey?.notes || ""
          }
        };

        return from(saveProject(postData)).pipe(
          mergeMap((data) => {
            if (data !== undefined) {
              if (window.location.pathname.includes("templateID")) {
                return [
                  saveJetliftMainReportDataAction(),
                  emptyaction()
                ];
              } else {
                toast.success("Project saved successfully", {
                  position: "top-right",
                  autoClose: 3000,
                  hideProgressBar: false,
                  closeOnClick: true,
                  pauseOnHover: true,
                  style: {
                    background: "#4CAF50",
                    color: "#fff",
                  },
                });

                setTimeout(() => {
                  if (window.location.pathname.includes("caseID")) {
                    window.location.href = `/project-details/projectID=${resolvedParentProjectId || data}/caseID=${data}`;
                  } else
                    window.location.href = `/project-details/projectID=${data}`;
                }, 4000);

                return [
                  saveJetliftMainReportDataAction(),
                  emptyaction()
                ];
              }
            }
            return [emptyaction()];
          }),
          catchError((error) => {
            toast.error("There was an error while saving project.", {
              position: "top-right",
              autoClose: 3000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              style: {
                background: "#4CAF50",
                color: "#fff",
              },
            });
            return of(console.error('There was an error while saving project.', error));
          })
        );
      }
    )
  );
};

const removeJetliftCase = (action$: any, state$: any) => {
  return action$.pipe(
    ofType("jetlift/calculations/removeJetliftCase"),
    withLatestFrom(
      state$.pipe(map((state: RootState) => state?.createProject?.jetliftProjectID))
    ),
    concatMap(([actionData, selectedProject]) => {
      return from(
        deleteJetliftCase(
          actionData
            ? actionData?.payload
            : selectedProject?.caseID ?? selectedProject?.projectID
        )
      ).pipe(
        map((result) => {
          return [];
        }),
      );
    })
  );
}

const copyJetliftProjectEpic = (action$: any, state$: any) => {
  return action$.pipe(
    ofType("jetlift/calculations/copyJetliftProject"),
    concatMap((actionData: any) => {
      const parentId = actionData?.payload?.parentId || "";
      return from(
        copyJetliftProjectService(actionData?.payload)
      ).pipe(
        mergeMap((result) => {
          if (result) {
            if (actionData?.payload?.IsCaseLevel) {
              toast.success("Project copied successfully.", {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                style: {
                  background: "#4CAF50",
                  color: "#fff",
                },
              });
            }
            return [
              getProjectAndAffiliations(parentId),
              emptyaction()
            ];
          }
          return [emptyaction()];
        }),
        catchError((error) => {
          toast.error("Error copying project.", {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            style: {
              background: "#f44336",
              color: "#fff",
            },
          });
          return of(console.error('Error copying project.', error));
        })
      );
    })
  );
}

const saveJetliftMainReportData = (action$: any, state$: any) => {
  return action$.pipe(
    ofType("jetlift/calculations/saveJetliftMainReportData"),
    withLatestFrom(
      state$.pipe(map((state: RootState) => state)),
      state$.pipe(map((state: RootState) => state?.createProject))
    ),
    switchMap(([actionData, sateData, projectTabData]) => {
      let mainReportData = getJetLiftMainReportDataFromUtility(sateData);

      return from(
        SaveJetLiftMainReportData(
          mainReportData.inputs,
          mainReportData.projectId,
          mainReportData.unitSystem
        )
      ).pipe(
        map((data) => {
          if (window.location.pathname.includes("templateID")) {
            toast.success("Project saved successfully", {
              position: "top-right",
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              style: {
                background: "#4CAF50",
                color: "#fff",
              },
            });
            setTimeout(() => {
              window.location.href = `/project-details/projectID=${mainReportData.projectId}`;
            }, 6000);
          }
          return emptyaction();
        }),
        catchError((error) => {
          console.error('Error saving jetlift main report data:', error);
          return of(emptyaction());
        })
      );
    })
  );
};

const getJetliftComparisonData = (action$: any, state$: any) => {
  return action$.pipe(
    ofType("jetlift/comparison/getJetliftComparisonData"),
    withLatestFrom(state$.pipe(map((state: RootState) => state?.jetlift?.comparison))),
    switchMap(([actionData, sateData]) => {
      return from(
        GetJetliftCaseComparisonData(
          sateData?.compareInput1,
          sateData?.compareInput2,
          sateData?.compareInput3
        )
      ).pipe(
        mergeMap((data) => {
          let case1Data = data?.caseComparisions?.filter(
            (item: any) => item?.caseId === sateData?.compareInput1
          );
          let case2Data = data?.caseComparisions?.filter(
            (item: any) => item?.caseId === sateData?.compareInput2
          );
          let case3Data = data?.caseComparisions?.filter(
            (item: any) => item?.caseId === sateData?.compareInput3
          );
          return [
            setComparisonData1(case1Data?.[0] ?? {}),
            setComparisonData2(case2Data?.[0] ?? {}),
            setComparisonData3(case3Data?.[0] ?? {}),
          ];
        }),
        catchError((error) => {
          return of(
            console.error("There was an error while fetching comparison data.")
          );
        })
      );
    })
  );
};

const downloadJLMainReportEpic = (action$: any, state$: any) => {
  return action$.pipe(
    ofType("jetlift/calculations/downloadJLMainReport"),
    withLatestFrom(state$.pipe(map((state: RootState) => state))),
    switchMap(([action, state]) => {
      const reportPayload = getJLReportDataFromUtility(state as RootState);
      const reportType = (action as { payload?: { reportType?: string } })?.payload?.reportType ?? "PDF";
      const payload = { ...reportPayload, reportType };
      return from(downloadJLMainReportService(payload)).pipe(
        map(() => {
          toast.success("Report downloaded successfully", {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            style: { background: "#4CAF50", color: "#fff" },
          });
          return [emptyaction(), setLoading(false)] as const;
        }),
        catchError(() => {
          toast.error("Failed to download report.", {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            style: { background: "#f44336", color: "#fff" },
          });
          return of([emptyaction(), setLoading(false)] as const);
        }),
        mergeMap((actions) => of(...actions)),
        startWith(setLoading(true))
      );
    })
  );
};

export const jetliftCalculationEpic = combineEpics(
  fetchProjectDetailsEpic,
  calculationsTemplate,
  SaveProject,
  saveJetliftMainReportData,
  downloadJLMainReportEpic,
  removeJetliftCase,
  copyJetliftProjectEpic,
  getJetliftComparisonData,
);
